"use client"

import { BlockchainIcon } from "@/app/blockchains/blockchain-icon/blockchain-icon"
import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { BlockchainView } from "@/app/blockchains/blockchain-view"
import { SpotlightMintWidget } from "@/app/drop/actions/spotlight-mint-widget"
import { DropDisplayMoleculeReatom } from "@/app/drop/store/drop-display-molecule.reatom"
import { VerifiedIcon } from "@/app/icons/verified"
import { PriceWrapper } from "@/app/price"
import type { MinimalDrop } from "@anaconda/core/drop/types"
import { Trans, useLingui } from "@lingui/react/macro"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { DropStatusBadge } from "../../../drop/status/drop-status"
import { Count } from "../../view/count"
import { DropsSpotlightStoreMolecule } from "./drops-spotlight-store.reatom"
import type { DropForSpotlight } from "./drops-spotlight-view"

export const DropTitle = reatomComponent(
  ({ drop, slideIndex }: { drop: MinimalDrop; slideIndex: number }) => {
    const { t } = useLingui()

    const { dropStatusAtom, dropStatusTextAtom } = useMolecule(
      DropDisplayMoleculeReatom,
    )

    const status = dropStatusAtom()
    const statusText = dropStatusTextAtom()

    return (
      <div className="flex flex-col gap-2">
        <DropStatusBadge status={status}>{t(statusText)}</DropStatusBadge>
        <div className="flex flex-col gap-2">
          <h3
            className="flex items-center gap-2 overflow-hidden text-nowrap text-lg lg:text-2xl"
            title={drop.title}
          >
            <span className="overflow-hidden text-ellipsis">{drop.title}</span>
            {drop.isVerified && (
              <VerifiedIcon className="h-[15px] w-[15px] shrink-0" />
            )}
          </h3>
        </div>
      </div>
    )
  },
  "DropTitle",
)

export const DropInfo = reatomComponent(
  ({
    drop,
    slideIndex,
  }: {
    drop: DropForSpotlight
    slideIndex: number
  }) => {
    const { reatomBlockchainChainName } = useMolecule(BlockchainsMoleculeReatom)
    const blockchainChainName = reatomBlockchainChainName()(drop.blockchain)

    // Compute isActive reactively by comparing with current active slide
    const { isSlideActive } = useMolecule(DropsSpotlightStoreMolecule)
    const isActive = isSlideActive(slideIndex)

    return (
      <div className="flex flex-col gap-5">
        {drop.author && (
          <div className="flex items-center gap-2">
            <span className="text-foreground/60">
              <Trans>by</Trans>
            </span>
            <span>{drop.author}</span>
            <span className="text-foreground/60">on</span>
            <BlockchainView
              icon={<BlockchainIcon blockchain={drop.blockchain} />}
              name={blockchainChainName}
            />
          </div>
        )}

        {isActive && <ActiveDropInfo drop={drop} />}
      </div>
    )
  },
  "DropInfo",
)

const ActiveDropInfo = reatomComponent(
  ({ drop }: { drop: DropForSpotlight }) => {
    const { activeSlide } = useMolecule(DropsSpotlightStoreMolecule)

    // Read atomized active slide data
    const slide = activeSlide()
    const { minted, currentPhase, fee, displayPrice } = slide

    if (!currentPhase || !displayPrice) return null

    return (
      <div className="flex items-center gap-x-6">
        <SpotlightMintWidget drop={drop} variant="filled" withPrice />
        <div className="flex items-center justify-between gap-x-6 text-sm">
          <InfoItem
            label={<Trans>Minted</Trans>}
            value={<Count value={minted} />}
          />
          {fee && fee.amount !== "0" ? (
            <InfoItem
              label={<Trans>Fee</Trans>}
              value={
                <PriceWrapper
                  currency={fee.currency}
                  amount={fee.amount}
                  minValuableNumber={{ value: 0.0001, label: "<0.0001" }}
                  displayZeroAsFree={false}
                />
              }
            />
          ) : null}
        </div>
      </div>
    )
  },
  "ActiveDropInfo",
)

function InfoItem({
  label,
  value,
}: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-foreground/80 text-xs">{label}</span>
      <span>{value}</span>
    </div>
  )
}
